import { ApiError } from "./errors";
import { callGateway, callGatewayBytes, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * The logo an operator's invoices print (owner 2026-10-29: "With trade name
 * please also add logo image"). A separate logo from the portal theme's; the
 * operator's Owner and Admin upload it, Billing and superadmin read
 * (radaccess v1.211, RBAC "invoicelogo"). radbilling prints the issuer's.
 */

export interface InvoiceLogo {
  sha256: string;
  contenttype: string;
  bytes: number;
  width: number;
  height: number;
  setby: string;
  updatedat: string;
}

const path = (ns: string, rest = "") => `/operator/${encodeURIComponent(ns)}/invoicelogo${rest}`;

/** What the operator's invoices print, or null for no logo. */
export async function getInvoiceLogo(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<InvoiceLogo | null> {
  try {
    const r = await callGateway<InvoiceLogo>(identity, config().gatewayBase, path(ns), signal ? { signal } : {});
    return r.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function getInvoiceLogoImage(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<Blob | null> {
  try {
    return await callGatewayBytes(identity, config().gatewayBase, path(ns, "/image"), signal ? { signal } : {});
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Stores a PNG/JPEG data URL (already shrunk) as the operator's invoice logo. */
export async function setInvoiceLogo(identity: CallerIdentity, ns: string, dataUrl: string): Promise<InvoiceLogo> {
  const r = await callGateway<InvoiceLogo>(identity, config().gatewayBase, path(ns), { method: "PUT", body: { image: dataUrl } });
  return r.data;
}

/** Invoices print no logo. Nothing is deleted: the image stays on record. */
export async function removeInvoiceLogo(identity: CallerIdentity, ns: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, path(ns), { method: "DELETE" });
}

export function invoiceLogoErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: unknown } | null)?.error;
    if (code === "logo_not_jpeg_or_png" || code === "logo_unreadable") return "Choose a PNG or JPEG logo.";
    if (code === "logo_too_large") return "That logo is too large. Choose a smaller file (at most 150 KB).";
    if (code === "logo_bad_size") return "That picture is too small. Choose a logo at least 16 pixels on each side.";
    if (code === "invoice_logos_not_available_yet") return "Invoice logos are not available yet. Try again later.";
    if (err.status === 403) return "Only the operator's Owner or Admin can change the invoice logo.";
  }
  if (err instanceof Error && err.message === "not-an-image") return "Choose a PNG or JPEG logo.";
  return "The logo could not be saved. Try again in a moment.";
}
